import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/language-context";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import * as Sentry from "@sentry/react";
import { Check, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Redirect } from "wouter";

interface Plan {
  id: string;
  name: string;
  amount: number;
  currency: string;
  interval: string;
}

export default function PlansPage() {
  const { t } = useLanguage();
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const { toast } = useToast();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await api.get<Plan[]>("/subscription/plans");
        // Sort plans: monthly first, or based on amount?
        // Typically monthly then yearly, but let's just keep the order from API
        setPlans(data);

        // Pre-pick the cycle chosen on the landing pricing card (landing.tsx handleSelectPlan),
        // else yearly, else first plan; storage can throw in private mode.
        let picked: string | null = null;
        try {
          picked = sessionStorage.getItem("selectedPlan");
        } catch {}
        const preferred =
          (picked === "monthly" && data.find((p) => p.interval === "month")) ||
          data.find((p) => p.interval === "year");
        setSelectedPlanId(preferred?.id || data[0]?.id || null);
      } catch (error: any) {
        toast({
          title: "Error",
          description: "Failed to load subscription plans. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingPlans(false);
      }
    };

    if (isAuthenticated) {
      fetchPlans();
    }
  }, [isAuthenticated, toast]);

  const handleCheckout = async () => {
    if (!selectedPlanId) return;

    setIsProcessing(true);
    try {
      const data = await api.post<{ url: string }>("/subscription/checkout", {
        priceId: selectedPlanId,
      });
      if (data.url) {
        Sentry.addBreadcrumb({
          category: "checkout",
          level: "info",
          message: "Redirecting to Stripe",
          data: { price_id: selectedPlanId },
        });
        window.location.href = data.url;
      } else {
        const err = new Error("Failed to create checkout session — empty url");
        Sentry.captureException(err, {
          tags: { source: "checkout", stage: "create_session", price_id: selectedPlanId },
        });
        throw err;
      }
    } catch (error: any) {
      // 5xx + network already captured by api.ts interceptor; only capture
      // here for non-axios throws (the empty-url branch above).
      toast({
        title: "Checkout Error",
        description:
          error.message || "Failed to start checkout. Please try again.",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  const includedFeatures = [
    "Unlimited guided sessions",
    "Personalized recommendations",
    "Journaling prompts",
    "Priority support",
    "Morning & evening practices",
    "Progress analytics",
    "Offline access",
    "Weekly new content",
  ];

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF68C3]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Redirect to="/signup" />;
  }

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#FFF8FA] to-[#FFF0F5]">
      {/* Header */}
      <header className="p-6 lg:p-8 flex items-center justify-between">
        <a href="/" className="inline-block">
          <span className="font-serif text-2xl font-medium text-gray-900">
            Evooluir
          </span>
        </a>
        {user && (
          <div className="text-sm text-gray-600">
            Welcome back,{" "}
            <span className="font-medium text-gray-900">
              {user.name || user.email}
            </span>
          </div>
        )}
      </header>

      <div className="max-w-2xl mx-auto px-6 pb-16">
        {/* Hero section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF68C3]/10 to-[#FFA98B]/10 border border-[#FF68C3]/20 mb-6">
            <Sparkles className="w-4 h-4 text-[#FF68C3]" />
            <span className="text-sm font-medium text-gray-700">
              Complete your subscription
            </span>
          </div>

          <h1 className="font-serif text-3xl lg:text-4xl font-medium text-gray-900 mb-3">
            Choose your wellness plan
          </h1>
          <p className="text-gray-600">
            Start with a 7-day free trial. Cancel anytime.
          </p>
        </div>

        {/* Plan selection card */}
        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-8 border border-gray-100">
          {isLoadingPlans ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF68C3] mb-4" />
              <p className="text-gray-500">Loading plans...</p>
            </div>
          ) : (
            <>
              {/* Plan Options */}
              <div className="space-y-3 mb-8">
                {plans.map((plan) => {
                  const isYearly = plan.interval === "year";
                  const isSelected = selectedPlanId === plan.id;
                  const monthlyAmount = isYearly
                    ? plan.amount / 12
                    : plan.amount;

                  return (
                    <div
                      key={plan.id}
                      className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? "border-[#FF68C3] bg-gradient-to-r from-[#FF68C3]/5 to-[#FFA98B]/5"
                          : "border-gray-200 hover:border-gray-300"
                      } ${isYearly ? "mt-4" : ""}`}
                      onClick={() => {
                        setSelectedPlanId(plan.id);
                        // Remember it, so a Stripe cancel or refresh comes back to this plan.
                        try {
                          sessionStorage.setItem(
                            "selectedPlan",
                            plan.interval === "month" ? "monthly" : "yearly",
                          );
                        } catch {}
                      }}
                      data-testid={`plan-${plan.id}`}
                    >
                      {isYearly && (
                        <div className="absolute -top-3 left-5 px-3 py-1 rounded-full bg-gradient-to-r from-[#FF68C3] to-[#FFA98B] text-xs font-semibold text-white">
                          Best Value
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-1">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              isSelected
                                ? "border-[#FF68C3] bg-[#FF68C3]"
                                : "border-gray-300"
                            }`}
                          >
                            {isSelected && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {plan.interval === "year"
                                ? "Yearly"
                                : plan.interval === "month"
                                  ? "Monthly"
                                  : plan.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {isYearly
                                ? `$${plan.amount.toFixed(2)} billed yearly`
                                : plan.interval === "month"
                                  ? "Monthly billing"
                                  : "Flexible billing"}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-gray-900">
                            ${monthlyAmount.toFixed(2)}
                          </span>
                          <span className="text-gray-500 text-sm">/mo</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* What's included */}
              <div className="mb-8 p-5 bg-gray-50 rounded-2xl">
                <h3 className="font-semibold text-gray-900 mb-4">
                  What's included:
                </h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                  {includedFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#FF68C3] flex-shrink-0" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <Button
                size="lg"
                className="w-full h-14 btn-gradient text-base font-semibold rounded-xl shadow-lg shadow-[#FF68C3]/25"
                onClick={handleCheckout}
                disabled={isProcessing || !selectedPlanId}
                data-testid="button-checkout"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Continue to Payment"
                )}
              </Button>

              <p className="text-center text-xs text-gray-500 mt-4">
                7-day free trial, then{" "}
                {selectedPlan?.interval === "year"
                  ? `$${selectedPlan.amount.toFixed(2)}/year`
                  : `$${selectedPlan?.amount.toFixed(2)}/month`}
                . Cancel anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
